<template>
  <section v-if="active" class="app-modal__section">
    <header class="app-modal__section-head contributor-fiscal-ident__head">
      <div class="contributor-fiscal-ident__head-copy">
        <h4>{{ text.modal.form.fiscalIdentification.title }}</h4>
        <p>{{ text.modal.form.fiscalIdentification.subtitle }}</p>
      </div>

      <label class="app-modal-toggle app-modal-toggle--primary contributor-fiscal-ident__default-toggle">
        <input v-model="form.default" type="checkbox" :true-value="1" :false-value="0" />
        <span>{{ text.modal.form.fiscalIdentification.fields.defaultFiscalData }}</span>
      </label>
    </header>

    <div class="app-modal__grid two-columns">
      <div class="app-modal-field">
        <span>{{ text.modal.form.fiscalIdentification.fields.contributorType }}</span>
        <multiselect
          v-model="form.type"
          :options="contributorTypeOptions"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="false"
          :allow-empty="false"
          :max-height="128"
          open-direction="below"
          :placeholder="text.modal.form.fiscalIdentification.placeholders.contributorType"
          class="app-modal-select"
        />
      </div>

      <label class="app-modal-field">
        <span>{{ taxIdentifierLabel }}<template v-if="isNationalType"> *</template></span>
        <input
          v-model.trim="form.taxIdentifier"
          type="text"
          class="form-control"
          autocomplete="off"
          :placeholder="text.modal.form.fiscalIdentification.placeholders.taxIdentifier || 'Captura RFC o identificador'"
          @blur="v$.form.taxIdentifier.$touch()"
          :class="{ 'is-invalid': v$.form.taxIdentifier.$error }"
        />
        <div class="invalid-feedback d-block" v-if="v$.form.taxIdentifier.$error">
          <span v-if="v$.form.taxIdentifier.$errors.length">{{ v$.form.taxIdentifier.$errors[0].$message }}</span>
        </div>
      </label>

      <label class="app-modal-field">
        <span>{{ text.modal.form.fiscalIdentification.fields.fiscalName }} *</span>
        <input
          v-model.trim="form.name"
          type="text"
          class="form-control"
          autocomplete="off"
          :placeholder="text.modal.form.fiscalIdentification.placeholders.fiscalName || 'Captura razón social'"
          @blur="v$.form.name.$touch()"
          :class="{ 'is-invalid': v$.form.name.$error }"
        />
        <div class="invalid-feedback d-block" v-if="v$.form.name.$error">
          <span v-if="v$.form.name.$errors.length">{{ v$.form.name.$errors[0].$message }}</span>
        </div>
      </label>

      <label class="app-modal-field">
        <span>{{ text.modal.form.fiscalIdentification.fields.fiscalZipCode }} *</span>
        <input
          v-model.trim="form.zipcode"
          type="text"
          class="form-control"
          autocomplete="off"
          :placeholder="text.modal.form.fiscalIdentification.placeholders.fiscalZipCode || 'Captura código postal'"
          @blur="v$.form.zipcode.$touch()"
          :class="{ 'is-invalid': v$.form.zipcode.$error }"
        />
        <div class="invalid-feedback d-block" v-if="v$.form.zipcode.$error">
          <span v-if="v$.form.zipcode.$errors.length">{{ v$.form.zipcode.$errors[0].$message }}</span>
        </div>
      </label>

      <div class="app-modal-field">
        <span>{{ text.modal.form.fiscalConfiguration.fields.fiscalRegime }} *</span>
        <multiselect
          v-model="form.regimen"
          :options="regimenOptions"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="false"
          :max-height="128"
          open-direction="below"
          :placeholder="text.modal.form.fiscalConfiguration.placeholders.fiscalRegime"
          class="app-modal-select contributor-fiscal-ident__truncate-select"
          :class="{ 'is-invalid': v$.form.regimen.$error }"
          @close="v$.form.regimen.$touch()"
        />
        <div class="invalid-feedback d-block" v-if="v$.form.regimen.$error">
          <span v-if="v$.form.regimen.$errors.length">{{ v$.form.regimen.$errors[0].$message }}</span>
        </div>
      </div>

      <div class="app-modal-field">
        <span>{{ text.modal.form.fiscalConfiguration.fields.cfdiUse }} *</span>
        <multiselect
          v-model="form.usoCfdi"
          :options="usoCfdiOptions"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="false"
          :max-height="128"
          open-direction="below"
          :placeholder="text.modal.form.fiscalConfiguration.placeholders.cfdiUse"
          class="app-modal-select contributor-fiscal-ident__truncate-select"
          :class="{ 'is-invalid': v$.form.usoCfdi.$error }"
          @close="v$.form.usoCfdi.$touch()"
        />
        <div class="invalid-feedback d-block" v-if="v$.form.usoCfdi.$error">
          <span v-if="v$.form.usoCfdi.$errors.length">{{ v$.form.usoCfdi.$errors[0].$message }}</span>
        </div>
      </div>

      <div class="app-modal-field">
        <span>{{ text.modal.form.fiscalConfiguration.fields.paymentMethod }}<template v-if="isNationalType"> *</template></span>
        <multiselect
          v-model="form.paymentMethod"
          :options="paymentMethodOptions"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="!isNationalType"
          :max-height="108"
          open-direction="below"
          :placeholder="text.modal.form.fiscalConfiguration.placeholders.paymentMethod"
          class="app-modal-select contributor-fiscal-ident__payment-form-select"
          :class="{ 'is-invalid': v$.form.paymentMethod.$error }"
          @close="v$.form.paymentMethod.$touch()"
        />
        <div class="invalid-feedback d-block" v-if="v$.form.paymentMethod.$error">
          <span v-if="v$.form.paymentMethod.$errors.length">{{ v$.form.paymentMethod.$errors[0].$message }}</span>
        </div>
      </div>

      <div class="app-modal-field">
        <span>{{ text.modal.form.fiscalConfiguration.fields.paymentMethodType }}<template v-if="isNationalType"> *</template></span>
        <multiselect
          v-model="form.paymentMethodType"
          :options="paymentMethodTypeOptions"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="!isNationalType"
          :max-height="128"
          open-direction="below"
          :placeholder="text.modal.form.fiscalConfiguration.placeholders.paymentMethodType"
          class="app-modal-select"
          :class="{ 'is-invalid': v$.form.paymentMethodType.$error }"
          @close="v$.form.paymentMethodType.$touch()"
        />
        <div class="invalid-feedback d-block" v-if="v$.form.paymentMethodType.$error">
          <span v-if="v$.form.paymentMethodType.$errors.length">{{ v$.form.paymentMethodType.$errors[0].$message }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('billingData');

export default {
  name: 'ContributorSectionFiscalIdentification',
  data() {
    return {
      text,
    };
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      required: true,
    },
    v$: {
      type: Object,
      required: true,
    },
    contributorTypeOptions: {
      type: Array,
      default: () => [],
    },
    taxIdentifierLabel: {
      type: String,
      default: '',
    },
    isNationalType: {
      type: Boolean,
      default: true,
    },
    regimenOptions: {
      type: Array,
      default: () => [],
    },
    usoCfdiOptions: {
      type: Array,
      default: () => [],
    },
    paymentMethodOptions: {
      type: Array,
      default: () => [],
    },
    paymentMethodTypeOptions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['constancia-selected'],
};
</script>

<style scoped>
.contributor-fiscal-ident__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.contributor-fiscal-ident__head-copy {
  min-width: 0;
}

.contributor-fiscal-ident__default-toggle {
  margin: 0;
  max-width: 360px;
}

.contributor-fiscal-ident__default-toggle input {
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  flex: 0 0 18px;
}

:deep(.contributor-fiscal-ident__payment-form-select .multiselect__content-wrapper) {
  max-height: 108px !important;
  overflow-y: auto !important;
}

:deep(.contributor-fiscal-ident__truncate-select .multiselect__tags) {
  min-width: 0;
}

:deep(.contributor-fiscal-ident__truncate-select .multiselect__single) {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.multiselect.is-invalid .multiselect__tags),
:deep(.multiselect.is-invalid .multiselect__single),
:deep(.multiselect.is-invalid .multiselect__input) {
  border-color: #dc3545 !important;
}

@media (max-width: 900px) {
  .contributor-fiscal-ident__head {
    flex-direction: column;
  }

  .contributor-fiscal-ident__default-toggle {
    max-width: none;
  }
}
</style>
